import MiniSearch from 'minisearch';
import { mirrorVersion, type Mirror } from './mirror.ts';

interface SearchDoc {
  id: string;
  title: string;
  tags: string;
  ingredients: string;
  cuisine: string;
}

let cached: { version: number; index: MiniSearch<SearchDoc> } | undefined;

/** Folds umlauts and accents so "gemuse" still finds "Gemüse" (and "Creme" finds "Crème"). */
export const foldTerm = (term: string) =>
  term.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

async function buildIndex(db: Mirror): Promise<MiniSearch<SearchDoc>> {
  const [recipes, lines] = await Promise.all([db.recipes.toArray(), db.lines.toArray()]);
  const names = new Map<string, string[]>();
  // Both languages (ADR 0010): "lentils" finds "Linsen-Bolognese" and the other way round.
  for (const line of lines) {
    const both = [line.name, line.translation?.name ?? ''];
    names.set(line.recipeId, [...(names.get(line.recipeId) ?? []), ...both]);
  }
  const index = new MiniSearch<SearchDoc>({
    fields: ['title', 'tags', 'ingredients', 'cuisine'],
    processTerm: foldTerm,
    searchOptions: {
      boost: { title: 3, tags: 2 },
      prefix: true,
      fuzzy: (term) => (term.length > 4 ? 0.2 : false),
      combineWith: 'AND',
    },
  });
  index.addAll(
    recipes.map((r) => ({
      id: r.id,
      title: [r.title, r.translation?.title ?? ''].join(' '),
      tags: [...r.tags, ...(r.translation?.tags ?? [])].join(' '),
      ingredients: (names.get(r.id) ?? []).join(' '),
      cuisine: [r.cuisine ?? '', r.translation?.cuisine ?? ''].join(' '),
    })),
  );
  return index;
}

/**
 * Offline search over the mirror (PLAN.md §9): title, tags, cuisine and ingredient names, prefix
 * and light fuzzy matching. Returns recipe ids, best match first; the index is rebuilt only
 * after the mirror changed.
 */
export async function searchMirror(db: Mirror, query: string): Promise<string[]> {
  const version = mirrorVersion();
  if (cached?.version !== version) cached = { version, index: await buildIndex(db) };
  return cached.index.search(query).map((hit) => String(hit.id));
}
